import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Zap, Clock, Volume2, Play } from "lucide-react";
import AnimatedBackground from "@/components/AnimatedBackground";
import { useIsMobile } from "@/hooks/use-mobile";
import SectionHeader from "@/components/SectionHeader";
const AIProductions = () => {
  const [activeVideo, setActiveVideo] = useState(0);
  const [videoWithSound, setVideoWithSound] = useState(false);
  const isMobile = useIsMobile();

  const handleVideoClick = () => {
    setVideoWithSound(true);
  };
  const handleVideoChange = (index: number) => {
    setActiveVideo(index);
    setVideoWithSound(false);
  };

  const videos = [{
    id: "1137892262",
    title: "FIRMENLADEN GmbH",
    mobileTitle: "Firmenladen"
  }, {
    id: "1137900417",
    title: "Hammer GmbH & Co. KG, Advanced Logistics",
    mobileTitle: "Hammer"
  }, {
    id: "1137901108",
    title: "Janssen Cosmetics GmbH",
    mobileTitle: "Janssen"
  }];

  const benefits = [{
    icon: Clock,
    text: "In unter 7 Tagen fertig"
  }, {
    icon: Zap,
    text: "Kein Drehtag notwendig"
  }, {
    icon: Sparkles,
    text: "High-end & aufmerksamkeitsstark"
  }];

  return (
    <section id="ki-productions" className="py-12 md:py-20 lg:py-24 bg-gradient-to-b from-background via-background to-background relative overflow-hidden">
      {/* Animated Background with Blue Dots */}
      <AnimatedBackground variant="blue" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.08),transparent_50%)]"></div>
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `
          linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px),
          linear-gradient(0deg, rgba(59,130,246,0.5) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px'
      }}></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-8 md:mb-12 animate-fade-in">
          <SectionHeader
            badge="✨ KI-Powered"
            title="KI Imagefilme & Reels"
            highlightedWord="Reels"
            description="Neu. Anders. Voller Vorteile. Mit KI erstellen wir hochwertige Markenfilme und Social-Reels – ohne Produktionsaufwand."
            className="mb-6 md:mb-8"
          />

          {/* Benefits */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-6 mb-8 md:mb-12 px-4">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-card border border-blue-500/30 rounded-full"
              >
                <benefit.icon className="w-4 md:w-5 h-4 md:h-5 text-blue-400 flex-shrink-0" />
                <span className="text-xs md:text-sm font-medium whitespace-nowrap">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Video Player */}
        <div className="max-w-5xl mx-auto mb-6 md:mb-8 px-4 md:px-0">
          <div 
            className={`${activeVideo === 2 ? 'aspect-[9/16] max-w-md' : 'aspect-video'} bg-muted rounded-lg overflow-hidden shadow-2xl shadow-blue-500/20 animate-fade-in mx-auto group cursor-pointer relative`} 
            onClick={handleVideoClick}
          >
            {/* Thumbnail Fallback - visible on mobile until tapped */}
            <div 
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-500 ${(isMobile && !videoWithSound) ? 'opacity-100' : (!isMobile ? 'opacity-0' : 'opacity-0')}`}
              style={{ backgroundImage: `url(https://vumbnail.com/${videos[activeVideo].id}.jpg)` }}
            />
            {/* On mobile: only load iframe after tap; on desktop: always load */}
            {(!isMobile || videoWithSound) && (
              <iframe 
                key={`${activeVideo}-${videoWithSound ? 'sound' : 'muted'}`} 
                src={`https://player.vimeo.com/video/${videos[activeVideo].id}?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&loop=1&muted=${videoWithSound ? '0' : '1'}&dnt=1`} 
                className="w-full h-full relative z-10" 
                frameBorder="0" 
                allow="autoplay; fullscreen; picture-in-picture" 
                title={`KI-Videoproduktion Aachen - ${videos[activeVideo].title}`}
              />
            )}
            {/* Play/Sound overlay */}
            {!videoWithSound && (
              <div className={`absolute inset-0 flex items-center justify-center z-20 ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity duration-300`}>
                <div className="bg-background/80 backdrop-blur-sm rounded-full p-4">
                  {isMobile ? <Play className="w-6 h-6 text-blue-400 fill-blue-400" /> : <Volume2 className="w-6 h-6 text-blue-400" />}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Project Selector */}
        <div className="flex justify-center animate-fade-in px-4">
          <div className="inline-flex items-center bg-blue-500/10 backdrop-blur-sm rounded-full p-1 border border-blue-500/30">
            {videos.map((video, index) => (
              <button 
                key={index} 
                onClick={() => handleVideoChange(index)} 
                className={`relative px-4 md:px-6 py-2 md:py-2.5 text-xs md:text-sm rounded-full transition-all duration-300 ${
                  activeVideo === index 
                    ? "bg-blue-500 text-white font-semibold shadow-lg shadow-blue-500/30" 
                    : "text-foreground/60 md:hover:text-foreground"
                }`}
              >
                <span className="md:hidden">{video.mobileTitle}</span>
                <span className="hidden md:inline">{video.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIProductions;